import React, {
  CSSProperties, ReactElement, useEffect, useRef,
} from 'react';
import { Button } from 'antd';
import Papa from 'papaparse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'react-toastify';
import { padStart } from 'lodash';
import { download, ExportDataElement } from '../../../util/util';
import { useAppSelector } from '../../../store/hooks';
import { generateListOfTimes, timeAsDecimal } from '../../../util/tstamp';

type HeatmapExportProps = {
  startDate: string;
  endDate: string;
  exportData: ExportDataElement[];
} & typeof defaultProps;

const defaultProps = {
  style: {} as CSSProperties,
};

const HeatmapExport = ({
  startDate,
  endDate,
  exportData,
  style,
}: HeatmapExportProps): ReactElement => {
  // const selectedDaysOfWeek = props.selectedDaysOfWeek;
  const availableIntersections = useAppSelector((state) => (
    state.entities.pm.availableIntersections));
  const selectedDaysOfWeek = useAppSelector((state) => state.entities.pm.selectedDaysOfWeek);
  // const exportData = useAppSelector((state) => state.entities.pm.exportData);
  const headersRaw = [{
    label: 'Location and type',
    key: 'locationType',
  }];
  let title = `PM_${startDate}_${endDate}.csv`;

  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const exportDataRef = useRef(exportData);
  const availableIntersectionsRef = useRef(availableIntersections);
  const selectedDaysOfWeekRef = useRef(selectedDaysOfWeek);

  const onDownloadClicked = () => {
    if (!exportDataRef.current.length) {
      toast.error('No data');
      return;
    }
    if (!selectedDaysOfWeek) {
      toast.error('No days of week selected');
    }

    const checkedDowStr = selectedDaysOfWeekRef.current
      .map((dow) => dow.shortLabel)
      .toString();
    title = `PM_${startDateRef.current}_${endDateRef.current}_${checkedDowStr}.csv`;

    const dataWithLocationType = exportDataRef.current.map((e) => {
      const intersection = availableIntersectionsRef.current
        .find((i) => i.intersectionId === e.intersectionId);
      const intersectionName = intersection ? intersection.intersectionName : '';
      return {
        locationType: `${intersectionName}-${e.direction}-${e.metric}`,
        intersectionName,
        ...e,
      };
    });
    const dataStr = Papa.unparse(dataWithLocationType, {
      header: true,
      columns: [
        'locationType',
        'intersectionName',
        'metric',
        'intersectionId',
        'direction',
        ...generateListOfTimes()
          .map(timeAsDecimal)
          .map((num) => `${padStart(num.toFixed(2))}`),
      ],
    });
    download(title, dataStr);
  };

  useHotkeys('d', onDownloadClicked);

  useEffect(() => {
    startDateRef.current = startDate;
    endDateRef.current = endDate;
    exportDataRef.current = exportData;
    availableIntersectionsRef.current = availableIntersections;
    selectedDaysOfWeekRef.current = selectedDaysOfWeek;
  }, [startDate, endDate, exportData, availableIntersections, selectedDaysOfWeek]);

  useEffect(() => {
    const timeList = [];
    for (let i = 0; i < 24; i++) {
      timeList.push(
        ...[i, i + 0.25, i + 0.5, i + 0.75],
      );
    }
    headersRaw.concat(timeList.map((time) => ({
      label: `${time}`,
      key: `${time}`,
    })));
  }, [exportData]);

  return (
    <Button
      onClick={onDownloadClicked}
      icon={<FontAwesomeIcon icon={faDownload} style={{ marginRight: '8px' }} />}
      style={{
        ...style,
        display: 'none',
      }}
      disabled={!exportData.length}
    >
      Export
    </Button>
  );
};

export default HeatmapExport;
